import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Col, Container, Card, CardBody } from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import { useNavigate } from "react-router-dom";
import PageHeaderActions from "Components/Common/PageHeaderActions";
import DeleteItemModal from "Components/Modals/DeleteItemModal";
import { useAppDispatch, useAppSelector } from "Components/Hooks/hooks";
import { deletePresetQuoteTemplate, getPresetQuoteList } from "slices/thunks";
import ActionMenu from "Components/Common/ActionMenu";
import OverlayGuide from "Components/Common/TourGuide/OverlayGuide";
import { useScreenTour } from "tour/useScreenTour";
import { tourElementsIds } from "tour/tourElementsIds";

const PresetQuotes = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { presetQuoteList, meta, isLoading } = useAppSelector(
    (state) => state?.presetQuote
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [templateId, setTemplateId] = useState<number>();
  const [searchText, setSearchText] = useState<string>("");
  const presetTour = useScreenTour("presetQuote");

  //Preset Quote Get Api Call
  useEffect(() => {
    const params = searchText
      ? { search: searchText }
      : { pageNo: currentPage };
    dispatch(getPresetQuoteList(params));
  }, [currentPage, dispatch, searchText]);

  // Column
  const columns = useMemo(
    () => [
      {
        header: "TEMPLATE NAME",
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },
      {
        header: "ITEMS",
        accessorKey: "items_count",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary">
            {cell.getValue()}
          </span>
        ),
      },

      {
        header: "DESCRIPTION",
        accessorKey: "description",
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => (
          <span className="fw-normal text-black2D fs-14 font-primary text-wrap line-clamp-2">
            {cell.getValue() ? cell.getValue() : "-"}
          </span>
        ),
      },
      {
        header: "ACTION",
        enableSorting: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;
          return (
            <ActionMenu
              onEdit={() => {
                navigate("/manage-preset-quotes", {
                  state: { templateId: rowData?.id },
                });
              }}
              onDelete={() => {
                setShowDeleteModal(true);
                setTemplateId(rowData?.id);
              }}
              isView
              onView={() => {
                navigate("/preset-quotes-details", {
                  state: { templateId: rowData?.id },
                });
              }}
            />
          );
        },
      },
    ],
    [navigate]
  );

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handleDeletePresetTemplate = useCallback(() => {
    dispatch(deletePresetQuoteTemplate({ templateId: templateId! }));
    setShowDeleteModal(false);
  }, [dispatch, templateId]);

  const handleSearch = useCallback((text: string) => {
    setSearchText(text);
  }, []);

  document.title = "Preset Quotes | EaziQuote";
  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="gap-3 d-flex flex-column">
            <PageHeaderActions
              title="Preset Quotes"
              subtitle="Manage all your quotes in one place"
              actions={[
                {
                  label: "New Preset",
                  onClick: () => {
                    navigate("/manage-preset-quotes");
                  },
                  leftIcon: <i className="ri-add-fill me-1 align-bottom"></i>,
                },
              ]}
            />

            <Col lg={12}>
              <Card className="br-10 bg-white">
                <CardBody
                  className="pt-0"
                  id={tourElementsIds.presetQuoteTour1}
                >
                  <div>
                    <TableContainer
                      columns={columns}
                      data={presetQuoteList ?? []}
                      isGlobalFilter={true}
                      divClass="table-responsive table-card mb-2 bg-white"
                      tableClass="align-middle table-nowrap table-borderless category-table"
                      thClass="text-grey89 fw-medium fs-14 font-primary bg-greyF5 category-table"
                      theadClass="table-light"
                      SearchPlaceholder="Search preset quotes"
                      onPageChange={handlePageChange}
                      customPageSize={meta?.per_page}
                      totalPages={meta?.last_page}
                      currentPage={meta?.current_page}
                      fromPage={meta?.from}
                      toPage={meta?.to}
                      totalItems={meta?.total}
                      isInitialLoading={isLoading && !presetQuoteList?.length}
                      isPaginationLoading={
                        isLoading && presetQuoteList?.length > 0
                      }
                      onSearch={handleSearch}
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </div>
        </Container>
      </div>
      <DeleteItemModal
        show={showDeleteModal}
        onCloseClick={() => {
          setShowDeleteModal(false);
        }}
        onDeleteClick={handleDeletePresetTemplate}
      />
      <OverlayGuide {...presetTour} />
    </React.Fragment>
  );
};

export default PresetQuotes;
